// DELETIONS BELL — the owner's record of every delete / cancel / restore (owner rule 2026-11-02)
//
// Every destructive action in the app (a payment cycle deleted or restored, an
// asset retired or deleted, a VAT payment, an expense or a category deleted, an
// EOI / Deal hard-deleted, a lead deleted) writes ONE Notification of type
// "delete" through lib/deleteNotify.js on the server. getVisibleNotifications
// answers that type to isOwner accounts alone, so this bell is mounted by the
// Header only for isOwnerFE(p.access) and simply lists what the server lets it
// read: "<Name> deleted <type> #<id> (<summary>) — <date 12h>", newest first.
//
// It owns no rule. It fetches on mount, on open and whenever the tab regains
// focus; unseen rows (the server's `seen` flag for this viewer) drive the badge
// and are marked seen by TYPE the moment the panel opens. apiFetch is handed
// in by the Header so this file carries no copy of the client's fetch helper.

import React, { useEffect, useRef, useState } from "react";
import { Trash2, X } from "lucide-react";

var PAGE = 50;

function ago(iso) {
  if (!iso) return "";
  var ms = Date.now() - new Date(iso).getTime();
  if (!isFinite(ms) || ms < 0) return "";
  var m = Math.floor(ms / 60000);
  if (m < 1) return "just now";
  if (m < 60) return m + "m ago";
  var h = Math.floor(m / 60);
  if (h < 24) return h + "h ago";
  var d = Math.floor(h / 24);
  return d + "d ago";
}

export default function DeletionBell(p) {
  var [open, setOpen] = useState(false);
  var [rows, setRows] = useState([]);
  var [loaded, setLoaded] = useState(false);
  var ref = useRef(null);

  var load = function () {
    if (!p.token) return;
    p.apiFetch("/api/notifications?type=delete&page=1&limit=" + PAGE, "GET", null, p.token)
      .then(function (r) { setRows((r && Array.isArray(r.data)) ? r.data : (Array.isArray(r) ? r : [])); setLoaded(true); })
      .catch(function () { setLoaded(true); });
  };

  useEffect(function () { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [p.token]);
  useEffect(function () {
    var onFocus = function () { load(); };
    window.addEventListener("focus", onFocus);
    return function () { window.removeEventListener("focus", onFocus); };
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [p.token]);
  useEffect(function () {
    if (!open) return;
    var fn = function (e) { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", fn);
    return function () { document.removeEventListener("mousedown", fn); };
  }, [open]);

  var unseen = rows.filter(function (n) { return !n.seen; }).length;

  var toggle = function () {
    var opening = !open;
    setOpen(opening);
    if (!opening) return;
    load();
    if (unseen > 0) {
      p.apiFetch("/api/notifications/mark-seen", "PUT", { type: "delete" }, p.token, p.csrfToken)
        .then(function () { setRows(function (prev) { return prev.map(function (n) { return Object.assign({}, n, { seen: true }); }); }); })
        .catch(function () {});
    }
  };

  var red = "#B91C1C", light = "#64748B", text = "#0F172A";
  return <div style={{ position: "relative" }} ref={ref} data-deletion-bell="1">
    <button type="button" onClick={toggle} title="Deletions" aria-label="Deletions"
      style={{ width: 36, height: 36, borderRadius: 9, border: "1px solid #E8ECF1", background: unseen > 0 ? "#FEF2F2" : "#fff", display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", position: "relative", padding: 0 }}>
      <Trash2 size={16} color={unseen > 0 ? red : light} />
      {unseen > 0 && <span style={{ position: "absolute", top: -2, right: -2, minWidth: 17, height: 17, borderRadius: 9, background: red, color: "#fff", fontSize: 9, fontWeight: 700, display: "flex", alignItems: "center", justifyContent: "center", padding: "0 4px" }}>{unseen > 99 ? "99+" : unseen}</span>}
    </button>
    {open && <div className="crm-notif-panel" style={{ position: "absolute", top: 46, right: 0, width: 420, maxWidth: "95vw", background: "#fff", borderRadius: 16, boxShadow: "0 16px 48px rgba(0,0,0,0.14), 0 0 0 1px rgba(0,0,0,0.04)", zIndex: 200, display: "flex", flexDirection: "column", maxHeight: "70vh", overflow: "hidden" }}>
      <div style={{ padding: "14px 18px", borderBottom: "1px solid #F1F5F9", display: "flex", justifyContent: "space-between", alignItems: "center", flexShrink: 0 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
          <Trash2 size={16} color={red} />
          <span style={{ fontWeight: 700, fontSize: 14, color: text }}>Deletions</span>
          {rows.length > 0 && <span style={{ background: "#FEF2F2", color: red, padding: "2px 8px", borderRadius: 10, fontSize: 11, fontWeight: 600 }}>{rows.length}</span>}
        </div>
        <button type="button" onClick={function () { setOpen(false); }} style={{ background: "none", border: "none", cursor: "pointer", color: light, display: "flex", padding: 4 }}><X size={15} /></button>
      </div>
      <div style={{ overflowY: "auto", flex: 1 }}>
        {!loaded && <div style={{ padding: 24, textAlign: "center", color: light, fontSize: 13 }}>Loading…</div>}
        {loaded && rows.length === 0 && <div style={{ padding: 32, textAlign: "center", color: light, fontSize: 13 }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}>🗑️</div>No deletions recorded
        </div>}
        {rows.map(function (n) {
          return <div key={n._id} data-deletion-row={n._id} style={{ padding: "11px 18px", borderBottom: "1px solid #F8FAFC", background: n.seen ? "#fff" : "#FFF5F5" }}>
            <div style={{ fontSize: 12.5, fontWeight: n.seen ? 500 : 700, color: text, lineHeight: 1.35, wordBreak: "break-word" }}>{n.title}</div>
            <div style={{ display: "flex", gap: 8, marginTop: 3, fontSize: 10, color: light }}>
              <span style={{ fontWeight: 600, color: red, background: "#FEF2F2", padding: "1px 6px", borderRadius: 4 }}>{n.reason || "deleted"}</span>
              {n.status && <span>{n.status}</span>}
              <span>{ago(n.eventTime || n.createdAt)}</span>
            </div>
          </div>;
        })}
      </div>
    </div>}
  </div>;
}
